import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  updateDoc,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { findUserByEmail } from './users';

/**
 * @typedef {Object} Community
 * @property {string} id
 * @property {string} community_name
 * @property {string} description
 * @property {string} category
 * @property {string} creator_id
 * @property {string[]} admin_ids
 * @property {string[]} members
 * @property {string[]} invited_members
 * @property {boolean} invite_only
 * @property {Array<unknown>} posts
 * @property {Array<unknown>} member_reports
 * @property {Array<unknown>} moderation_actions
 */

/**
 * @typedef {Object} CommunityPost
 * @property {string} id
 * @property {string} content
 * @property {string} authorId
 * @property {string} authorName
 * @property {number|null} createdAt
 */

const safeString = (value) => (typeof value === 'string' ? value : '');

const safeArray = (value) => (Array.isArray(value) ? value : []);

const uniqueStrings = (values) => [...new Set(
  safeArray(values)
    .map((value) => safeString(value).trim())
    .filter(Boolean)
)];

const safeBoolean = (value) => Boolean(value);

const createAuditId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const safeTimestamp = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

export const parseCommunityPost = (rawPost, index = 0) => {
  if (typeof rawPost === 'string') {
    return {
      id: `legacy-post-${index}`,
      content: rawPost,
      authorId: '',
      authorName: '',
      createdAt: null,
    };
  }

  const safePost = rawPost && typeof rawPost === 'object' ? rawPost : {};

  return {
    id: safeString(safePost.id).trim() || `post-${index}`,
    content: safeString(safePost.content),
    authorId: safeString(safePost.authorId || safePost.userId),
    authorName: safeString(safePost.authorName || safePost.username || safePost.creator),
    createdAt: safeTimestamp(safePost.createdAt || safePost.time || safePost.timestamp),
  };
};

/**
 * Safely normalizes legacy and malformed community documents.
 * Legacy documents can contain only a `name` field.
 *
 * @param {{ id?: string, data?: () => Record<string, unknown> } | Record<string, unknown>} source
 * @param {string} [fallbackId]
 * @returns {Community}
 */
export const parseCommunityDocument = (source, fallbackId = '') => {
  const rawData = typeof source?.data === 'function' ? source.data() || {} : source || {};
  const id = typeof source?.id === 'string' ? source.id : fallbackId;
  const communityName = safeString(rawData.community_name).trim() || safeString(rawData.name).trim();
  const creatorId = safeString(rawData.creator_id).trim();
  const members = uniqueStrings(rawData.members);
  const adminIds = uniqueStrings([creatorId, ...safeArray(rawData.admin_ids)]);

  return {
    id,
    community_name: communityName,
    description: safeString(rawData.description),
    category: safeString(rawData.category),
    creator_id: creatorId,
    admin_ids: adminIds,
    members,
    invited_members: uniqueStrings(rawData.invited_members),
    invite_only: safeBoolean(rawData.invite_only),
    posts: safeArray(rawData.posts).map((post, index) => parseCommunityPost(post, index)),
    member_reports: safeArray(rawData.member_reports),
    moderation_actions: safeArray(rawData.moderation_actions),
  };
};

const isCommunityAdmin = (community, userId) => Boolean(
  userId && (community?.creator_id === userId || safeArray(community?.admin_ids).includes(userId))
);

const getCommunitySnapshot = async (communityId) => {
  if (!communityId) {
    throw new Error('Community id is required.');
  }

  const snapshot = await getDoc(doc(db, 'communities', communityId));
  if (!snapshot.exists()) {
    throw new Error('Community not found.');
  }

  return snapshot;
};

/** The account to invite: exact email first, then lowercased. Throws if none exists. */
const findInvitee = async (email) => {
  const normalizedEmail = safeString(email).trim();
  if (!normalizedEmail) {
    throw new Error('A valid email address is required.');
  }
  const invitee = await findUserByEmail(normalizedEmail)
    || (normalizedEmail.toLowerCase() !== normalizedEmail ? await findUserByEmail(normalizedEmail.toLowerCase()) : null);
  if (!invitee) {
    throw new Error('No account was found for that email address.');
  }
  return invitee;
};

export const createCommunity = async (userId, communityDetails = {}) => {
  if (!userId) {
    throw new Error('A valid user is required to create a community.');
  }

  const communityName = safeString(
    communityDetails.community_name || communityDetails.name
  ).trim();

  if (!communityName) {
    throw new Error('Community name is required.');
  }

  const communityRef = doc(collection(db, 'communities'));
  const userRef = doc(db, 'users', userId);
  const payload = {
    community_name: communityName,
    description: safeString(communityDetails.description).trim(),
    category: safeString(communityDetails.category).trim(),
    creator_id: userId,
    admin_ids: [userId],
    members: [userId],
    invited_members: [],
    invite_only: safeBoolean(communityDetails.invite_only),
    posts: [],
    member_reports: [],
    moderation_actions: [],
  };

  const batch = writeBatch(db);
  batch.set(communityRef, payload);
  batch.set(
    userRef,
    {
      joined_communities: arrayUnion(communityRef.id),
    },
    { merge: true }
  );

  await batch.commit();

  return parseCommunityDocument({ id: communityRef.id, data: () => payload });
};

export const joinCommunity = async (userId, communityId) => {
  if (!userId || !communityId) {
    throw new Error('User id and community id are required.');
  }

  const communitySnapshot = await getCommunitySnapshot(communityId);
  const community = parseCommunityDocument(communitySnapshot);

  if (community.members.includes(userId)) {
    return;
  }

  if (community.invite_only && !community.invited_members.includes(userId)) {
    throw new Error('This community is invite only. An admin must invite you before you can join.');
  }

  const communityRef = doc(db, 'communities', communityId);
  const userRef = doc(db, 'users', userId);
  const batch = writeBatch(db);

  batch.set(
    communityRef,
    {
      members: arrayUnion(userId),
      invited_members: arrayRemove(userId),
    },
    { merge: true }
  );
  batch.set(
    userRef,
    {
      joined_communities: arrayUnion(communityId),
    },
    { merge: true }
  );

  await batch.commit();
};

export const leaveCommunity = async (userId, communityId) => {
  if (!userId || !communityId) {
    throw new Error('User id and community id are required.');
  }

  const communityRef = doc(db, 'communities', communityId);
  const userRef = doc(db, 'users', userId);
  const batch = writeBatch(db);

  batch.set(
    communityRef,
    {
      members: arrayRemove(userId),
      admin_ids: arrayRemove(userId),
    },
    { merge: true }
  );
  batch.set(
    userRef,
    {
      joined_communities: arrayRemove(communityId),
    },
    { merge: true }
  );

  await batch.commit();
};

export const inviteCommunityMember = async (actorUserId, communityId, inviteeEmail) => {
  if (!actorUserId || !communityId) {
    throw new Error('Actor and community are required.');
  }

  const communitySnapshot = await getCommunitySnapshot(communityId);
  const community = parseCommunityDocument(communitySnapshot);

  if (!community.invite_only) {
    throw new Error('Only invite-only communities need invitations.');
  }

  if (!isCommunityAdmin(community, actorUserId)) {
    throw new Error('Only community admins can invite members.');
  }

  const invitee = await findInvitee(inviteeEmail);

  if (community.members.includes(invitee.id)) {
    throw new Error('That user is already a member of this community.');
  }

  if (community.invited_members.includes(invitee.id)) {
    throw new Error('That user already has a pending invite.');
  }

  await updateDoc(doc(db, 'communities', communityId), {
    invited_members: arrayUnion(invitee.id),
    moderation_actions: arrayUnion({
      id: createAuditId('invite'),
      type: 'invite',
      actorId: actorUserId,
      targetUserId: invitee.id,
      reason: '',
      createdAt: Date.now(),
    }),
  });

  return invitee;
};

export const promoteCommunityAdmin = async (actorUserId, communityId, targetUserId) => {
  if (!actorUserId || !communityId || !targetUserId) {
    throw new Error('Actor, community, and target user are required.');
  }

  const communitySnapshot = await getCommunitySnapshot(communityId);
  const community = parseCommunityDocument(communitySnapshot);

  if (community.creator_id !== actorUserId) {
    throw new Error('Only the community creator can promote admins.');
  }

  if (!community.members.includes(targetUserId)) {
    throw new Error('Only current community members can become admins.');
  }

  if (community.admin_ids.includes(targetUserId)) {
    throw new Error('That member is already an admin.');
  }

  await updateDoc(doc(db, 'communities', communityId), {
    admin_ids: arrayUnion(targetUserId),
    moderation_actions: arrayUnion({
      id: createAuditId('promote'),
      type: 'promote-admin',
      actorId: actorUserId,
      targetUserId,
      reason: '',
      createdAt: Date.now(),
    }),
  });
};

export const removeCommunityMember = async (actorUserId, communityId, targetUserId, reason = '') => {
  if (!actorUserId || !communityId || !targetUserId) {
    throw new Error('Actor, community, and target user are required.');
  }

  const communitySnapshot = await getCommunitySnapshot(communityId);
  const community = parseCommunityDocument(communitySnapshot);
  const actorIsCreator = community.creator_id === actorUserId;
  const actorIsAdmin = isCommunityAdmin(community, actorUserId);
  const targetIsAdmin = community.admin_ids.includes(targetUserId);

  if (!actorIsAdmin) {
    throw new Error('Only community admins can remove members.');
  }

  if (!community.members.includes(targetUserId)) {
    throw new Error('That user is not an active community member.');
  }

  if (targetUserId === actorUserId) {
    throw new Error('Use Leave Community if you want to remove yourself.');
  }

  if (targetUserId === community.creator_id) {
    throw new Error('The community creator cannot be removed.');
  }

  if (!actorIsCreator && targetIsAdmin) {
    throw new Error('Only the creator can remove another admin.');
  }

  const communityRef = doc(db, 'communities', communityId);
  const userRef = doc(db, 'users', targetUserId);
  const batch = writeBatch(db);

  batch.set(
    communityRef,
    {
      members: arrayRemove(targetUserId),
      admin_ids: arrayRemove(targetUserId),
      invited_members: arrayRemove(targetUserId),
      moderation_actions: arrayUnion({
        id: createAuditId('remove'),
        type: 'remove-member',
        actorId: actorUserId,
        targetUserId,
        reason: safeString(reason).trim(),
        createdAt: Date.now(),
      }),
    },
    { merge: true }
  );

  batch.set(
    userRef,
    {
      joined_communities: arrayRemove(communityId),
    },
    { merge: true }
  );

  await batch.commit();
};

export const reportCommunityMember = async (reporterUserId, communityId, targetUserId, reason) => {
  const trimmedReason = safeString(reason).trim();

  if (!reporterUserId || !communityId || !targetUserId) {
    throw new Error('Reporter, community, and target user are required.');
  }

  if (!trimmedReason) {
    throw new Error('Please include a reason for the report.');
  }

  const communitySnapshot = await getCommunitySnapshot(communityId);
  const community = parseCommunityDocument(communitySnapshot);

  if (!community.members.includes(reporterUserId)) {
    throw new Error('You must be a member of the community to submit a report.');
  }

  if (!community.members.includes(targetUserId)) {
    throw new Error('That user is not an active member of this community.');
  }

  if (reporterUserId === targetUserId) {
    throw new Error('You cannot report yourself.');
  }

  await updateDoc(doc(db, 'communities', communityId), {
    member_reports: arrayUnion({
      id: createAuditId('report'),
      reporterId: reporterUserId,
      targetUserId,
      reason: trimmedReason,
      createdAt: Date.now(),
    }),
  });
};

export const subscribeToUserCommunities = (userId, callback) => {
  if (!userId) {
    callback([]);
    return () => {};
  }

  const communitiesQuery = query(
    collection(db, 'communities'),
    where('members', 'array-contains', userId)
  );

  return onSnapshot(
    communitiesQuery,
    (snapshot) => {
      const communities = snapshot.docs.map((snapshotDoc) => parseCommunityDocument(snapshotDoc));
      callback(communities);
    },
    (error) => {
      console.error('Failed to subscribe to user communities', error);
      callback([]);
    }
  );
};

/** Every community, parsed. */
export const listCommunities = async () => {
  const snapshot = await getDocs(collection(db, 'communities'));
  return snapshot.docs.map((snapshotDoc) => parseCommunityDocument(snapshotDoc));
};

export const subscribeToAllCommunities = (callback) => onSnapshot(
  collection(db, 'communities'),
  (snapshot) => {
    const communities = snapshot.docs.map((snapshotDoc) => parseCommunityDocument(snapshotDoc));
    callback(communities);
  },
  (error) => {
    console.error('Failed to subscribe to all communities', error);
    callback([]);
  }
);

export const subscribeToCommunity = (communityId, callback) => {
  if (!communityId) {
    callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, 'communities', communityId),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback(parseCommunityDocument(snapshot));
    },
    (error) => {
      console.error('Failed to subscribe to community detail', error);
      callback(null);
    }
  );
};

export const addCommunityPost = async (communityId, author, content) => {
  const trimmedContent = safeString(content).trim();
  const authorId = safeString(author?.id || author?.uid).trim();

  if (!communityId || !authorId || !trimmedContent) {
    throw new Error('Community id, author, and content are required.');
  }

  await updateDoc(doc(db, 'communities', communityId), {
    posts: arrayUnion({
      id: `${authorId}-${Date.now()}`,
      content: trimmedContent,
      authorId,
      authorName: safeString(author?.username || author?.name || author?.displayName).trim(),
      createdAt: Date.now(),
    }),
  });
};